import { FormEvent, useEffect, useMemo, useState } from 'react';
import { toast } from 'react-toastify';
import { AxiosError } from 'axios';
import { LoadingSpinner } from '../ui/LoadingSpinner';
import { SupportToneBadge } from './SupportToneBadge';
import { SupportAiSuggestButton } from './SupportAiSuggestButton';
import { ticketStatusTone } from '../../utils/supportDeskBadges';
import {
  SupportDeskBroadcastResult,
  SupportDeskProblemTicket,
  broadcastSupportDeskProblemReply,
  listSupportDeskProblemTickets,
  suggestSupportDeskMessage,
} from '../../services/supportDeskApi';

const STATUS_LABELS: Record<string, string> = {
  New: 'Novo',
  Triaged: 'Triado',
  InProgress: 'Em andamento',
  PendingCustomer: 'Aguardando cliente',
  Resolved: 'Resolvido',
  Closed: 'Fechado',
  Reopened: 'Reaberto',
};

const CLOSED = new Set(['Resolved', 'Closed']);

type SupportBroadcastComposerProps = {
  problemId: string;
  problemCode?: string | null;
  workaround?: string | null;
  /** Ticket de contexto para a sugestão de IA (geralmente o aberto na mesa). */
  contextTicketId?: string;
  onClose: () => void;
  onCompleted?: () => void;
};

function outcomeTone(outcome: string): 'success' | 'caution' | 'danger' | 'neutral' {
  if (outcome === 'Sent') return 'success';
  if (outcome === 'Skipped') return 'caution';
  if (outcome === 'Failed') return 'danger';
  return 'neutral';
}

function outcomeLabel(outcome: string): string {
  if (outcome === 'Sent') return 'Enviado';
  if (outcome === 'Skipped') return 'Ignorado';
  if (outcome === 'Failed') return 'Falhou';
  return outcome;
}

export function SupportBroadcastComposer({
  problemId,
  problemCode,
  workaround,
  contextTicketId,
  onClose,
  onCompleted,
}: SupportBroadcastComposerProps) {
  const [tickets, setTickets] = useState<SupportDeskProblemTicket[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedIds, setSelectedIds] = useState<string[]>([]);
  const [body, setBody] = useState('');
  const [useWorkaround, setUseWorkaround] = useState(Boolean(workaround?.trim()));
  const [onlyOpenTickets, setOnlyOpenTickets] = useState(true);
  const [sending, setSending] = useState(false);
  const [suggesting, setSuggesting] = useState(false);
  const [result, setResult] = useState<SupportDeskBroadcastResult | null>(null);

  const aiTicketId = contextTicketId || selectedIds[0] || tickets[0]?.id;

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setResult(null);
    void listSupportDeskProblemTickets(problemId)
      .then((items) => {
        if (cancelled) return;
        setTickets(items);
        const openIds = items.filter((item) => !CLOSED.has(item.status)).map((item) => item.id);
        setSelectedIds(openIds.length > 0 ? openIds : items.map((item) => item.id));
      })
      .catch(() => {
        if (!cancelled) {
          toast.error('Falha ao carregar tickets do problema.');
          setTickets([]);
        }
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [problemId]);

  const visibleTickets = useMemo(() => {
    if (!onlyOpenTickets) return tickets;
    return tickets.filter((item) => !CLOSED.has(item.status));
  }, [tickets, onlyOpenTickets]);

  useEffect(() => {
    setSelectedIds((current) => current.filter((id) => visibleTickets.some((ticket) => ticket.id === id)));
  }, [visibleTickets]);

  function toggleTicket(ticketId: string): void {
    setSelectedIds((current) =>
      current.includes(ticketId)
        ? current.filter((id) => id !== ticketId)
        : [...current, ticketId],
    );
  }

  function toggleAll(select: boolean): void {
    setSelectedIds(select ? visibleTickets.map((ticket) => ticket.id) : []);
  }

  async function handleSuggestWithAi(): Promise<void> {
    if (!aiTicketId || suggesting || sending) return;
    setSuggesting(true);
    try {
      const hintParts = [body.trim(), useWorkaround && workaround?.trim() ? `Workaround: ${workaround.trim()}` : '']
        .filter(Boolean)
        .join('\n\n');
      const resultSuggestion = await suggestSupportDeskMessage(
        aiTicketId,
        'Broadcast',
        hintParts || undefined,
      );
      setBody(resultSuggestion.text);
      toast.success('Sugestão de mensagem gerada. Revise antes de enviar.');
    } catch (err) {
      const detail = (err as AxiosError<{ detail?: string }>)?.response?.data?.detail;
      toast.error(detail ?? 'Não foi possível sugerir a mensagem com IA.');
    } finally {
      setSuggesting(false);
    }
  }

  async function handleSubmit(event: FormEvent): Promise<void> {
    event.preventDefault();
    const hasBody = body.trim().length > 0;
    const hasWorkaround = useWorkaround && Boolean(workaround?.trim());
    if (!hasBody && !hasWorkaround) {
      toast.error('Informe a mensagem ou use o workaround do problema.');
      return;
    }
    if (selectedIds.length === 0) {
      toast.error('Selecione ao menos um ticket.');
      return;
    }

    setSending(true);
    setResult(null);
    try {
      const response = await broadcastSupportDeskProblemReply(problemId, {
        body: hasBody ? body.trim() : undefined,
        useWorkaround,
        onlyOpenTickets,
        ticketIds: selectedIds,
      });
      setResult(response);
      if (response.failedCount === 0) {
        toast.success(
          `Resposta enviada a ${response.sentCount} ticket(s)${
            response.skippedCount > 0 ? ` (${response.skippedCount} ignorado(s))` : ''
          }.`,
        );
      } else {
        toast.warning(
          `Enviados: ${response.sentCount}. Falhas: ${response.failedCount}. Ignorados: ${response.skippedCount}.`,
        );
      }
      onCompleted?.();
    } catch {
      toast.error('Falha ao enviar a resposta em massa.');
    } finally {
      setSending(false);
    }
  }

  return (
    <div className="flex h-full min-h-0 flex-col border-t border-secondary-200 bg-white">
      <div className="flex items-start justify-between gap-3 border-b border-secondary-100 px-4 py-3">
        <div>
          <h3 className="text-sm font-semibold text-secondary-900">Responder ao grupo</h3>
          <p className="text-xs text-secondary-500">
            {problemCode ? `${problemCode} · ` : ''}
            mesma mensagem pública para os tickets selecionados
          </p>
        </div>
        <button type="button" className="erp-btn-secondary text-xs" onClick={onClose}>
          Fechar
        </button>
      </div>

      {loading ? (
        <div className="flex flex-1 items-center justify-center py-10">
          <LoadingSpinner />
        </div>
      ) : (
        <form onSubmit={(event) => void handleSubmit(event)} className="flex min-h-0 flex-1 flex-col">
          <div className="min-h-0 flex-1 space-y-4 overflow-y-auto px-4 py-4">
            <div>
              <div className="mb-2 flex flex-wrap items-center justify-between gap-2">
                <span className="text-xs font-semibold uppercase tracking-wide text-secondary-500">
                  Destinatários ({selectedIds.length}/{visibleTickets.length})
                </span>
                <div className="flex gap-2 text-xs">
                  <button type="button" className="text-primary-700 hover:underline" onClick={() => toggleAll(true)}>
                    Todos
                  </button>
                  <button type="button" className="text-secondary-600 hover:underline" onClick={() => toggleAll(false)}>
                    Nenhum
                  </button>
                </div>
              </div>
              {visibleTickets.length === 0 ? (
                <p className="rounded-lg bg-secondary-50 px-3 py-2 text-sm text-secondary-600">
                  Nenhum ticket elegível neste problema.
                </p>
              ) : (
                <ul className="max-h-40 space-y-1 overflow-y-auto rounded-lg border border-secondary-200">
                  {visibleTickets.map((ticket) => (
                    <li key={ticket.id}>
                      <label className="flex cursor-pointer items-start gap-2 px-3 py-2 text-sm hover:bg-secondary-50">
                        <input
                          type="checkbox"
                          className="mt-1"
                          checked={selectedIds.includes(ticket.id)}
                          onChange={() => toggleTicket(ticket.id)}
                        />
                        <span className="min-w-0 flex-1">
                          <span className="font-mono text-xs font-semibold text-primary-700">
                            {ticket.ticketCode}
                          </span>
                          <span className="ml-2 text-secondary-800">{ticket.title}</span>
                          <span className="mt-1 flex flex-wrap gap-1">
                            <SupportToneBadge tone={ticketStatusTone(ticket.status)}>
                              {STATUS_LABELS[ticket.status] ?? ticket.status}
                            </SupportToneBadge>
                            <span className="text-xs text-secondary-500">{ticket.companyName}</span>
                          </span>
                        </span>
                      </label>
                    </li>
                  ))}
                </ul>
              )}
            </div>

            <label className="block">
              <span className="mb-1 flex items-center justify-between gap-2">
                <span className="erp-label mb-0">Mensagem</span>
                {aiTicketId ? (
                  <SupportAiSuggestButton
                    suggesting={suggesting}
                    disabled={sending || loading}
                    onClick={() => void handleSuggestWithAi()}
                    title="Sugerir resposta em massa com IA"
                  />
                ) : null}
              </span>
              <textarea
                className="erp-input mt-1 w-full text-sm"
                rows={4}
                value={body}
                onChange={(event) => setBody(event.target.value)}
                placeholder="Texto enviado a todos os selecionados..."
                disabled={sending || suggesting}
              />
            </label>

            <div className="flex flex-wrap gap-4 text-sm text-secondary-700">
              <label className="inline-flex items-center gap-2">
                <input
                  type="checkbox"
                  checked={useWorkaround}
                  onChange={(event) => setUseWorkaround(event.target.checked)}
                  disabled={!workaround?.trim()}
                />
                Usar workaround se a mensagem estiver vazia
              </label>
              <label className="inline-flex items-center gap-2">
                <input
                  type="checkbox"
                  checked={onlyOpenTickets}
                  onChange={(event) => setOnlyOpenTickets(event.target.checked)}
                />
                Somente tickets abertos
              </label>
            </div>

            {useWorkaround && workaround?.trim() ? (
              <p className="rounded-lg border border-secondary-200 bg-secondary-50 px-3 py-2 text-xs text-secondary-600 whitespace-pre-wrap">
                Workaround: {workaround.trim()}
              </p>
            ) : null}

            {result ? (
              <div className="space-y-2 rounded-lg border border-secondary-200 p-3">
                <div className="flex flex-wrap gap-2 text-xs">
                  <SupportToneBadge tone="success">Enviados: {result.sentCount}</SupportToneBadge>
                  <SupportToneBadge tone="caution">Ignorados: {result.skippedCount}</SupportToneBadge>
                  <SupportToneBadge tone="danger">Falhas: {result.failedCount}</SupportToneBadge>
                </div>
                <ul className="max-h-36 space-y-1 overflow-y-auto text-sm">
                  {result.items.map((item) => (
                    <li
                      key={item.ticketId}
                      className="flex flex-wrap items-center justify-between gap-2 border-b border-secondary-100 py-1 last:border-0"
                    >
                      <span className="font-mono text-xs text-secondary-700">{item.ticketCode}</span>
                      <span className="flex min-w-0 flex-1 flex-wrap items-center justify-end gap-2">
                        <SupportToneBadge tone={outcomeTone(item.outcome)}>
                          {outcomeLabel(item.outcome)}
                        </SupportToneBadge>
                        {item.errorMessage ? (
                          <span className="truncate text-xs text-secondary-500">{item.errorMessage}</span>
                        ) : null}
                      </span>
                    </li>
                  ))}
                </ul>
              </div>
            ) : null}
          </div>

          <div className="flex flex-wrap items-center justify-end gap-2 border-t border-secondary-100 px-4 py-3">
            <button type="button" className="erp-btn-secondary text-sm" onClick={onClose}>
              Cancelar
            </button>
            <button
              type="submit"
              className="erp-btn-primary text-sm"
              disabled={sending || suggesting || selectedIds.length === 0}
            >
              {sending ? 'Enviando...' : `Enviar a ${selectedIds.length} ticket(s)`}
            </button>
          </div>
        </form>
      )}
    </div>
  );
}
